import { parseOrThrow } from '@shared/utils/validation-error';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  writeBatch,
  query,
  where,
  orderBy,
} from 'firebase/firestore';
import { z } from 'zod';
import { db } from '@/firebase/config';
import type { PaymentDate, ID } from '@shared/types';
import { buildBaseFields, buildUpdateFields, fromSnapshot, writeAuditLog } from '@shared/utils';

const paymentDateCreateSchema = z.object({
  name: z.string().min(1, '支払日名は必須です'),
  formula: z.string().min(1, '計算式は必須です'),
  priority: z.number().default(0),
});

export type PaymentDateCreateInput = z.infer<typeof paymentDateCreateSchema>;
export type PaymentDateUpdateInput = Partial<PaymentDateCreateInput>;

const COL = 'paymentDates';

// デフォルト固定支払日。Firestoreには初期保存せず、取得時にクライアント側で先頭に合成する。
// 編集・削除は不可（固定値）。表示順のみ並び替え・永続化できる。
const DEFAULT_PAYMENT_DATE_SEEDS = [
  { name: '翌月末支払い', formula: '翌月末' },
  { name: '翌々5日支払', formula: '翌々月5日' },
  { name: '翌々10日支払い', formula: '翌々月10日' },
  { name: '随時', formula: '随時' },
] as const;
const DEFAULT_PAYMENT_DATE_PREFIX = 'paymentdate-default-';

export const DEFAULT_PAYMENT_DATES: PaymentDate[] = DEFAULT_PAYMENT_DATE_SEEDS.map((s, i) => ({
  id: `${DEFAULT_PAYMENT_DATE_PREFIX}${i}`,
  name: s.name,
  formula: s.formula,
  priority: i,
  isDefault: true,
  isDeleted: false,
  createdBy: 'system',
  updatedBy: 'system',
})) as unknown as PaymentDate[];

export function isDefaultPaymentDate(id: ID): boolean {
  return id.startsWith(DEFAULT_PAYMENT_DATE_PREFIX);
}

export async function fetchPaymentDates(): Promise<PaymentDate[]> {
  const q = query(
    collection(db, COL),
    where('isDeleted', '==', false),
    orderBy('priority')
  );
  const snap = await getDocs(q);
  const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as PaymentDate);
  // 永続化されたデフォルト支払日の並び順(priority)を反映する。
  // 未シードのデフォルトは正規の既定順にフォールバックする。
  const persisted = new Map(
    all.filter((x) => isDefaultPaymentDate(x.id)).map((x) => [x.id, x.priority])
  );
  const defaults = DEFAULT_PAYMENT_DATES.map((x) => ({
    ...x,
    priority: persisted.get(x.id) ?? x.priority,
  })).sort((a, b) => a.priority - b.priority);
  const custom = all.filter((x) => !isDefaultPaymentDate(x.id));
  // デフォルト固定支払日を先頭に、ユーザー登録支払日を後続に並べる
  return [...defaults, ...custom];
}

/**
 * デフォルト固定支払日の表示順を永続化する。
 * Firestore上に未シードの場合は setDoc(merge) で作成しつつ priority を更新する。
 * 種別名・計算式・isDefaultは固定値として常に書き込む。管理者のみ許可。
 */
export async function reorderDefaultPaymentDates(
  orderedIds: ID[],
  userId: ID
): Promise<void> {
  const batch = writeBatch(db);
  orderedIds.forEach((id, index) => {
    const def = DEFAULT_PAYMENT_DATES.find((x) => x.id === id);
    if (!def) return;
    batch.set(
      doc(db, COL, id),
      {
        name: def.name,
        formula: def.formula,
        priority: index,
        isDefault: true,
        ...buildBaseFields(userId),
      },
      { merge: true }
    );
  });
  await batch.commit();
  await writeAuditLog({
    entityType: 'paymentDate',
    entityId: 'default-order',
    action: 'update',
    userId,
    after: { order: orderedIds },
  });
}

export async function fetchPaymentDate(id: ID): Promise<PaymentDate | null> {
  const snap = await getDoc(doc(db, COL, id));
  return fromSnapshot<PaymentDate>(snap);
}

export async function createPaymentDate(
  data: PaymentDateCreateInput,
  userId: ID
): Promise<PaymentDate> {
  const validated = parseOrThrow(paymentDateCreateSchema, data);
  const payload = { ...validated, ...buildBaseFields(userId) };
  const ref = await addDoc(collection(db, COL), payload);
  const created = { id: ref.id, ...payload } as unknown as PaymentDate;
  await writeAuditLog({
    entityType: 'paymentDate',
    entityId: ref.id,
    action: 'create',
    userId,
    after: validated,
  });
  return created;
}

export async function updatePaymentDate(
  id: ID,
  data: PaymentDateUpdateInput,
  userId: ID
): Promise<PaymentDate> {
  if (isDefaultPaymentDate(id)) throw new Error('デフォルト支払日は編集できません。');
  const validated = paymentDateCreateSchema.partial().parse(data);
  const ref = doc(db, COL, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('支払日が見つかりません。');
  const before = snap.data();
  const patch = { ...validated, ...buildUpdateFields(userId) };
  await updateDoc(ref, patch);
  await writeAuditLog({
    entityType: 'paymentDate',
    entityId: id,
    action: 'update',
    userId,
    before,
    after: validated,
  });
  return { id, ...before, ...patch } as unknown as PaymentDate;
}

export async function deletePaymentDate(id: ID, userId: ID): Promise<void> {
  if (isDefaultPaymentDate(id)) throw new Error('デフォルト支払日は削除できません。');
  const ref = doc(db, COL, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('支払日が見つかりません。');
  await updateDoc(ref, { isDeleted: true, ...buildUpdateFields(userId) });
  await writeAuditLog({
    entityType: 'paymentDate',
    entityId: id,
    action: 'delete',
    userId,
    before: snap.data(),
  });
}
